import { Inject, Injectable } from '@angular/core';
import { AlertViewModel, ItemsViewModel } from '../../src/typings/api';
import { AppRootScope } from '../../src/typings/AppRootScope';
import { AlertLevel, IAlertLevelIcon } from '../../src/typings/models';
import { $q, lodash } from '../ajs-upgraded-providers';
import { AppContextService } from '../appContext/AppContextService';
import { NotificationService } from '../notification/NotificationService';
import { ProxyService } from '../proxy/ProxyService';
import { AlertColors } from './typings/AlertColors';
import { AlertLevels } from './typings/models';
import { SubscriptionTypes } from './typings/SubscriptionTypes';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    public static $name: string = 'alertService';

    constructor(
        @Inject($q) private $q: angular.IQService,
        @Inject('$rootScope') private $rootScope: AppRootScope,
        private appContextService: AppContextService,
        @Inject(lodash) private lodash: _.LoDashStatic,
        private notificationService: NotificationService,
        private proxyService: ProxyService
    ) {
    }

    public initialize(): angular.IPromise<Array<ItemsViewModel<AlertViewModel>>> {
        return this.getAlertsByLocationsForCurrentUser().then((alerts: Array<ItemsViewModel<AlertViewModel>>) => {
            // Only location users will get these, so we still have to do an individual call when loading locations for NOLA users
            this.appContextService.setLocationAlerts(alerts);
            return alerts;
        });
    }

    public getAlertTitle(alertLevel: number): string {
        switch (alertLevel) {
            case AlertLevel.Info:
                return 'Info';
            case AlertLevel.Ok:
                return 'Ok';
            case AlertLevel.Warning:
                return 'Warning';
            case AlertLevel.Alert:
                return 'Alert';
            default:
                return null;
        }
    }

    public setAlertIcon(alertLevel: AlertLevel): IAlertLevelIcon {
        let settings: IAlertLevelIcon;
        switch (alertLevel) {
            case AlertLevel.Info:
                settings = {
                    cls: 'alert-level-info',
                    color: this.getAlertColor(alertLevel)
                };
                break;
            case AlertLevel.Ok:
                settings = {
                    cls: 'alert-level-ok',
                    color: this.getAlertColor(0)
                };
                break;
            case AlertLevel.Warning:
                settings = {
                    cls: 'alert-level-warning',
                    color: this.getAlertColor(alertLevel)
                };
                break;
            case AlertLevel.Alert:
                settings = {
                    cls: 'alert-level-alert',
                    color: this.getAlertColor(alertLevel)
                };
                break;
            default:
                return null;
        }
        settings.title = 'Alert Level: ' + this.getAlertTitle(alertLevel);
        return settings;
    }

    /**
     * Determine the highest alert color in an array of alerts
     * @param alerts The list of alerts
     */
    public getHighestAlertColor(alerts: AlertViewModel[]): string {
        if (!alerts || !alerts.length) {
            return this.getAlertColor(AlertLevels.Normal);
        }

        /* This only works because the alertLevels enum is in order -- don't break it. */
        let highestAlertLevel = Math.max.apply(Math, alerts.map((alert: AlertViewModel) => alert.alertLevel));

        return this.getAlertColor(highestAlertLevel);
    }

    /**
     * Determine the highest alert color for a given areaId
     * @param areaId The areaId being considered
     */
    public getHighestAlertColorForArea(areaId: number): string {
        let filteredAlerts = this.getAlertsByAreaId(areaId);
        return this.getHighestAlertColor(filteredAlerts);
    }

    /**
     * Determine the highest alert color for a given locationId
     * @param locationId The locationId being considered
     */
    public getHighestAlertColorForLocation(locationId: number): string {
        let locationAlerts = this.appContextService.getLocationAlerts(locationId);
        return this.getHighestAlertColor(locationAlerts);
    }

    /**
     * Determine the appropriate color to display for a given alert level
     * @param alertLevel An integer denoting the alert level (common.alerts.AlertLevels)
     */
    public getAlertColor(alertLevel: number): string {
        const map = {
            [AlertLevels.Normal]: AlertColors.OK,
            [AlertLevels.Warning]: AlertColors.Warning,
            [AlertLevels.Alert]: AlertColors.Error
        };

        return this.$rootScope.currentTheme.name + '-' + map[alertLevel];
    }

    public getAlertTotals(data: AlertViewModel[]): AlertViewModel {
        var alertLevel = 0;
        var alertCount = 0;

        if (data.length > 0) {
            // aggregate...
            var maxAlertLevel = this.lodash.maxBy(data, (i: AlertViewModel) => {
                return i.alertLevel;
            });

            if (maxAlertLevel !== -Infinity) {
                alertLevel = maxAlertLevel.alertLevel;
            }
            alertCount = data.length;
        }

        return  {
            alertCount: alertCount,
            alertLevel: alertLevel
        } as AlertViewModel;
    }

    public getAlertTotalsForArea(areaId: number): number {
        if (!this.appContextService.loadedLocationAlerts) {
            return 0;
        }

        var filteredAlerts = this.getAlertsByAreaId(areaId);

        var totals = this.getAlertTotals(filteredAlerts);
        return totals.alertCount;
    }

    public getAlertTotalsForLocation(locationId: number): number {
        let locationAlerts = this.appContextService.getLocationAlerts(locationId);

        if (!locationAlerts) {
            return 0;
        }

        return locationAlerts.length;
    }

    public getAlertsByLocationsForCurrentUser(): angular.IPromise<Array<ItemsViewModel<AlertViewModel>>> {
        return this.proxyService.request({
            endPoint: 'location', route: 'getAlertsByLocationsForCurrentUser',
        }).asArray<ItemsViewModel<AlertViewModel>>();
    }

    public startAlertAggregateUpdates(locationId: number): angular.IPromise<void> {
        return this.notificationService.startUpdates(locationId, SubscriptionTypes.AlertAggregate, null, null);
    }

    public stopAlertAggregateUpdates(locationId: number): angular.IPromise<void> {
        return this.notificationService.stopUpdates(locationId, SubscriptionTypes.AlertAggregate, null);
    }

    private getAlertsByAreaId(areaId: number): AlertViewModel[] {
        return this.lodash.filter(this.appContextService.loadedLocationAlerts, (a: AlertViewModel) => a.areaId === areaId);
    }
}
